
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { 
  FormField, FormItem, FormLabel, FormControl, FormMessage 
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { enhancedInputValidator } from '@/utils/enhancedInputValidator';

interface AccountInfoSectionProps {
  form: UseFormReturn<any>;
  passwordRequirements: {
    length: boolean;
    uppercase: boolean;
    number: boolean;
    special: boolean;
  };
  onUsernameBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  onEmailBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  onSponsorBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  sponsorValid: boolean;
  showOnlyEmailAndPassword?: boolean;
}

const AccountInfoSection = ({ 
  form, 
  passwordRequirements,
  onUsernameBlur,
  onEmailBlur,
  onSponsorBlur,
  sponsorValid,
  showOnlyEmailAndPassword = false
}: AccountInfoSectionProps) => {
  const { t } = useLanguage();
  
  // Check if password field has been touched and doesn't meet all requirements
  const passwordValue = form.watch('password') || '';
  const showPasswordRequirements = passwordValue.length >= 6 && 
    (!passwordRequirements.length || !passwordRequirements.uppercase || 
     !passwordRequirements.number || !passwordRequirements.special);

  return (
    <div className="space-y-4">
      <h3 hidden className="text-lg font-medium text-neutral-300">{t('auth.accountInfo')}</h3>
      
      {showOnlyEmailAndPassword && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
          <FormField
            control={form.control}
            name="sponsor"
            render={({ field }) => (
              <FormItem className="space-y-2">
                <FormLabel className="text-purple-400">
                  {t('auth.sponsor')} {t('auth.sponsorMandatory')}
                </FormLabel>
                <FormControl>
                  <Input 
                    {...field} 
                    placeholder={t('auth.sponsorPlaceholder')}
                    className="bg-white/10 border-white/20 text-white placeholder:text-neutral-400 h-10"
                    onChange={(e) => {
                      const sanitized = enhancedInputValidator.sanitizeText(e.target.value, 20);
                      field.onChange(sanitized);
                      form.clearErrors('sponsor');
                    }}
                    onBlur={(e) => {
                      field.onBlur();
                      onSponsorBlur(e);
                    }}
                  />
                </FormControl>
                <FormMessage className="text-red-400" />
              </FormItem>
            )}
          />
          {!sponsorValid && (
            <div className="flex items-center pb-2">
              <Link 
                to="/contact-us" 
                className="text-base text-purple-400 hover:text-purple-300 underline"
              >
                {t('auth.needSponsor')}
              </Link>
            </div>
          )}
        </div>
      )}

      <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${!sponsorValid && showOnlyEmailAndPassword ? 'opacity-50 pointer-events-none' : ''}`}>
        {!showOnlyEmailAndPassword && (
          <>
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-neutral-300">{t('auth.username')}</FormLabel>
                  <FormControl>
                    <Input 
                      {...field} 
                      autoComplete="username"
                      disabled={!sponsorValid && showOnlyEmailAndPassword}
                      className="bg-white/10 border-white/20 text-white placeholder:text-neutral-400"
                      onChange={(e) => {
                        field.onChange(e);
                        form.clearErrors('username');
                      }}
                      onBlur={(e) => {
                        field.onBlur();
                        onUsernameBlur(e);
                      }}
                    />
                  </FormControl>
                  <FormMessage className="text-red-400" />
                </FormItem>
              )}
            />

            <div className="hidden md:block"></div>
          </>
        )}

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-neutral-300">{t('auth.email')}</FormLabel>
              <FormControl>
                <Input 
                  {...field} 
                  type="email" 
                  autoComplete="email"
                  disabled={!sponsorValid && showOnlyEmailAndPassword}
                  placeholder={t('auth.emailPlaceholderGeneric')}
                  className="bg-white/10 border-white/20 text-white placeholder:text-neutral-400"
                  onChange={(e) => {
                    field.onChange(e);
                    form.clearErrors('email');
                  }}
                  onBlur={(e) => {
                    field.onBlur();
                    onEmailBlur(e);
                  }}
                />
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="confirm_email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-neutral-300">{t('auth.confirmEmail')}</FormLabel>
              <FormControl>
                <Input 
                  {...field} 
                  type="email" 
                  disabled={!sponsorValid && showOnlyEmailAndPassword}
                  placeholder={t('auth.emailPlaceholderGeneric')}
                  className="bg-white/10 border-white/20 text-white placeholder:text-neutral-400"
                  onChange={(e) => {
                    field.onChange(e);
                    form.clearErrors('confirm_email');
                  }}
                />
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-neutral-300">{t('auth.password')}</FormLabel>
              <FormControl>
                <PasswordInput 
                  {...field} 
                  autoComplete="new-password"
                  disabled={!sponsorValid && showOnlyEmailAndPassword}
                  className="bg-white/10 border-white/20 text-white placeholder:text-neutral-400"
                  onChange={(e) => {
                    field.onChange(e);
                    form.clearErrors('password');
                  }}
                />
              </FormControl>
              <FormMessage className="text-red-400" />
              {showPasswordRequirements && (
                <div className="text-xs text-neutral-400 mt-1">
                  <p className="text-neutral-300">{t('auth.passwordRequirements')}</p>
                  <ul className="list-disc pl-4 mt-1 space-y-1">
                    <li className={passwordRequirements.length ? 'text-green-400' : 'text-gray-400'}>
                      {t('auth.passwordValidationMinLength')}
                    </li>
                    <li className={passwordRequirements.uppercase ? 'text-green-400' : 'text-gray-400'}>
                      {t('auth.passwordValidationUppercase')}
                    </li>
                    <li className={passwordRequirements.number ? 'text-green-400' : 'text-gray-400'}>
                      {t('auth.passwordValidationNumber')}
                    </li>
                    <li className={passwordRequirements.special ? 'text-green-400' : 'text-gray-400'}>
                      {t('auth.passwordValidationSpecial')}
                    </li>
                  </ul>
                </div>
              )}
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="confirm_password"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-neutral-300">{t('auth.confirmPassword')}</FormLabel>
              <FormControl>
                <PasswordInput 
                  {...field} 
                  autoComplete="new-password"
                  disabled={!sponsorValid && showOnlyEmailAndPassword}
                  className="bg-white/10 border-white/20 text-white placeholder:text-neutral-400"
                  onChange={(e) => {
                    field.onChange(e);
                    form.clearErrors('confirm_password');
                  }}
                />
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
};

export default AccountInfoSection;
